import React, { useMemo } from 'react';
import clsx from 'clsx';
import type { MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';
import { beatPulse, effectEnvelope } from 'fym-shared/src/music24/musicVideoEffectFrames';
import { TypedMemo } from '../../../common/types';
import {
  effectColour,
  effectLineAt,
  intensityOf,
  lineWords,
  numParam,
  outBack,
  textParam,
  type PreviewLyricLine,
} from '../previewEffectsMath';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for these parts of the editor; each part uses its own slice of it */
import styles from './PreviewEffectSketches.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricSketchProps {
  effect: MusicVideoEffect;
  sec: number;
  accent: string;
  lines: readonly PreviewLyricLine[];
  beats: readonly number[];
}

/** A word pops in over this long. */
const POP_SEC = 0.18;

const PLACE_CLASS = { center: styles.lyricCenter, top: styles.lyricTop, bottom: styles.lyricBottom } as const;

/**
 * A lyric moment (the sketch of `LyricEffects.tsx` of the render). Kinetic lyric: each word of the
 * line pops in on its time, the sung word gets an accent bar. Big hook lyric: the line in huge
 * upper-case type, the sung words solid and the words to come as outlines, a hit on every beat. The
 * editor has no word times, so the words share the time of the line evenly. The caption of the line
 * goes while the moment shows it (`isLineShownByEffect`).
 */
const LyricSketchComponent: React.FC<LyricSketchProps> = ({ effect, sec, accent, lines, beats }) => {
  const line = effectLineAt(effect, lines, sec);
  const words = useMemo(() => (line === null ? [] : lineWords(line)), [line]);
  if (line === null || words.length === 0) {
    return null;
  }
  const intensity = intensityOf(effect);
  const envelope = effectEnvelope(effect, sec);
  const sungIndex = words.reduce((last, word, index) => (word.startSec <= sec ? index : last), -1);

  if (effect.kind === 'lyricHook') {
    const scale = numParam(effect, 'scale', 1);
    const hit = 1 + 0.05 * intensity * beatPulse(sec, beats);
    const colour = effectColour(effect, accent, 'accent');
    return (
      <div className={styles.hookScrim} style={{ opacity: envelope }}>
        <div className={styles.hook} style={{ fontSize: `${9 * scale}cqw`, transform: `scale(${hit})` }}>
          {words.map((word, index) => (
            <span
              key={`${word.startSec}-${word.text}`}
              className={clsx(styles.hookWord, index > sungIndex && styles.hookWordToCome)}
              style={index > sungIndex ? { WebkitTextStrokeColor: colour } : { color: colour }}
            >
              {word.text}
            </span>
          ))}
        </div>
      </div>
    );
  }

  const place = textParam(effect, 'place', 'center');
  const colour = effectColour(effect, accent, 'white');
  return (
    <div
      className={clsx(styles.kinetic, PLACE_CLASS[place === 'top' || place === 'bottom' ? place : 'center'])}
      style={{ opacity: envelope }}
    >
      {words.map((word, index) => {
        const age = sec - word.startSec;
        if (age < 0) {
          return null;
        }
        const pop = outBack(Math.min(1, age / POP_SEC), 1.2 + 1.6 * intensity);
        return (
          <span
            key={`${word.startSec}-${word.text}`}
            className={styles.kineticWord}
            style={{ color: colour, transform: `scale(${pop})` }}
          >
            {word.text}
            {index === sungIndex && <i className={styles.kineticBar} style={{ background: accent }} />}
          </span>
        );
      })}
    </div>
  );
};

export const LyricSketch = TypedMemo(LyricSketchComponent);
